/**
 * useNfcBridge (Welle 6)
 *
 * Verbindet sich zu ws://127.0.0.1:50670 (lokaler Windows-NFC-Client).
 * Bei Empfang von `nfcRead`-Events wird der Payload via onEvent
 * an den Aufrufer weitergereicht. Reconnect mit Backoff.
 *
 * Nicht-blockierend: bei nicht erreichbarem WS bleibt der Call unberührt.
 */

import { useEffect, useRef } from 'react';

export interface NfcReadPayload {
  success: boolean;
  pan?: string;
  bancName?: string;
  blz?: string;
  bic?: string;
  expirationDate?: string;
  chipCardNumber?: string;
  bancLocation?: string;
  accountNumber?: string;
}

export interface NfcEvent {
  eventType: 'nfcRead';
  version: string;
  data: NfcReadPayload;
}

interface UseNfcBridgeOpts {
  enabled: boolean;
  url?: string;
  onEvent: (payload: NfcReadPayload) => void;
  onStatusChange?: (status: 'connecting' | 'connected' | 'disconnected') => void;
}

export function useNfcBridge({ enabled, url = 'ws://127.0.0.1:50670', onEvent, onStatusChange }: UseNfcBridgeOpts) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const stoppedRef = useRef(false);

  // Refs für aktuelle callbacks (damit reconnect-loop nicht neu aufgebaut wird)
  const onEventRef = useRef(onEvent);
  const onStatusChangeRef = useRef(onStatusChange);
  onEventRef.current = onEvent;
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    if (!enabled) return;
    stoppedRef.current = false;

    function connect() {
      if (stoppedRef.current) return;
      onStatusChangeRef.current?.('connecting');
      console.log('[nfc] connecting', url);
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        console.warn('[nfc] WebSocket-Konstruktor schlug fehl', e);
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[nfc] connected');
        reconnectAttemptRef.current = 0;
        onStatusChangeRef.current?.('connected');
      };

      ws.onmessage = (msg) => {
        let evt: NfcEvent;
        try {
          evt = typeof msg.data === 'string' ? JSON.parse(msg.data) : null;
        } catch (e) {
          console.warn('[nfc] invalid JSON, verworfen');
          return;
        }
        if (!evt || evt.eventType !== 'nfcRead' || !evt.data) {
          console.warn('[nfc] event ohne erkannten Typ', evt);
          return;
        }
        console.log('[nfc] nfcRead empfangen, success=', evt.data.success);
        try {
          onEventRef.current(evt.data);
        } catch (e) {
          console.warn('[nfc] onEvent-handler warf', e);
        }
      };

      ws.onerror = (e) => {
        console.warn('[nfc] error', (e as any)?.message ?? '(kein detail)');
      };

      ws.onclose = () => {
        console.log('[nfc] disconnected');
        onStatusChangeRef.current?.('disconnected');
        wsRef.current = null;
        scheduleReconnect();
      };
    }

    function scheduleReconnect() {
      if (stoppedRef.current) return;
      const attempt = ++reconnectAttemptRef.current;
      const delay = Math.min(30000, 1000 * Math.pow(1.5, Math.min(attempt, 8)));
      console.log(`[nfc] reconnect in ${Math.round(delay)} ms (versuch ${attempt})`);
      reconnectTimerRef.current = window.setTimeout(connect, delay);
    }

    connect();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, [enabled, url]);
}
